import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FolderCog, Save, Package, Loader2, Download, Eye, Upload, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import CursoTabsNav from "../components/CursoTabsNav";
import DtpAuditPanel from "../components/DtpAuditPanel";

/**
 * DTP — Dossier Técnico Pedagógico.
 *
 * Dois blocos: (a) form para editar dados do curso relevantes ao DTP; (b)
 * lista de capítulos com selecção + geração de ZIP.
 */
const FIELD_GROUPS = [
  { title: "Identificação", fields: ["NomeCurso", "Edicao", "CursoNoSigo", "AreaTexto", "DataRealizacaoTexto"] },
  { title: "Local & Entidade", fields: ["Local", "FormacaoInstalacoes", "MoradaContrato", "LocalEntidade", "EntidadeFormadora"] },
  { title: "Formador", fields: ["Formador", "FormadorNif", "FormadorHabilitacoes"] },
  { title: "Estrutura Pedagógica", fields: ["ObjetivoGeral", "ObjetivosEspecificos", "Metodologia", "AvaliacaoDesc", "RecursosDidaticos"] },
  { title: "Recrutamento (Mod. 06)", fields: ["RecrutamentoDescricao"] },
  { title: "Seleção dos Formandos (Mod. 07)", fields: ["SelecaoDescricao", "SelecaoResponsavel"] },
  { title: "Documentos de Apoio (Mod. 27)", fields: ["DocumentosApoioDescricao"] },
  { title: "Ocorrências e Plano de Ação (Mod. 20 — Secção 11.2)", fields: ["OcorrenciasPlanoAcao"] },
  { title: "Registo de Ocorrências (Mod. 13) — 1 linha por evento, formato: DATA | OCORRÊNCIA | ASSINATURA", fields: ["RegistoOcorrenciasTexto"] },
  { title: "Certificados no SIGO (Mod. 21 — Secção 11.1)", fields: ["CertificadosNoSigo"] },
  { title: "Actas de Reunião extra (Mod. 14 — Secção 12.1)", fields: ["AtasReunioesTexto"] },
  { title: "Coordenação (participantes por defeito, separado por vírgula)", fields: ["Coordenacao"] },
  { title: "Mod. 14 — Fase 1: Reunião de abertura da ação",
    fields: ["Mod14FaseAberturaDesc", "Mod14FaseAberturaInterv"] },
  { title: "Mod. 14 — Fase 2: Ponto de situação na abertura do curso",
    fields: ["Mod14FaseSituacaoDesc", "Mod14FaseSituacaoInterv"] },
  { title: "Mod. 14 — Fase 3: Análise do funcionamento da ação",
    fields: ["Mod14FaseAnaliseDesc", "Mod14FaseAnaliseInterv"] },
  { title: "Mod. 14 — Fase 4: Encerramento da ação",
    fields: ["Mod14FaseEncerramentoDesc", "Mod14FaseEncerramentoInterv"] },
  { title: "Sumário Global & Avaliação", fields: ["SumarioFim", "AvaliacaoGlobalAcao", "Aval1", "Aval2"] },
  { title: "Horário / Sessões", fields: ["Horario", "Sessoes"] },
  { title: "Datas DTP", fields: ["DataContrato", "DataDtp1", "DataDtp2", "DataDtp3", "DataDtp4"] },
  { title: "Sugestões locais (opcional)", fields: ["EstacionamentoSugerido", "RestaurantesSugeridos"] },
  { title: "Empresa (só formações in-company)", fields: ["EmpresaNome", "EmpresaNif", "EmpresaSede", "EmpresaRepresentadaPor", "EmpresaQualidade"] },
];

const LONG_TEXT = new Set(["ObjetivoGeral", "ObjetivosEspecificos", "Metodologia", "AvaliacaoDesc",
  "RecursosDidaticos", "SumarioFim", "AvaliacaoGlobalAcao", "Aval1", "Aval2", "Sessoes",
  "Horario", "MoradaContrato", "EstacionamentoSugerido", "RestaurantesSugeridos",
  "RecrutamentoDescricao", "SelecaoDescricao", "DocumentosApoioDescricao",
  "OcorrenciasPlanoAcao", "RegistoOcorrenciasTexto", "CertificadosNoSigo",
  "AtasReunioesTexto",
  "Mod14FaseAberturaDesc", "Mod14FaseSituacaoDesc",
  "Mod14FaseAnaliseDesc", "Mod14FaseEncerramentoDesc"]);
const DATE_FIELDS = new Set(["DataContrato", "DataDtp1", "DataDtp2", "DataDtp3", "DataDtp4"]);


function UploadRow({ label, hint, tipo, status, busy, onUpload, onRemove }) {
  const has = !!(status && status.path);
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`dtp-upload-${tipo}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1B1B1B]">{label}</div>
        <div className="text-xs text-[#978D78] mt-0.5">{hint}</div>
        {has && (
          <a
            href={status.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-[#A8843C] hover:underline"
            data-testid={`dtp-upload-${tipo}-link`}
          >
            <ExternalLink className="w-3 h-3"/> Abrir PDF actual
          </a>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold cursor-pointer ${busy ? "bg-[#F3F0E7] text-[#978D78]" : "bg-[#A8843C] text-white hover:bg-[#8f6e2f]"}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
          {has ? "Substituir" : "Carregar"}
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            disabled={busy}
            onChange={(e) => { if (e.target.files?.[0]) onUpload(e.target.files[0]); e.target.value = ""; }}
            data-testid={`dtp-upload-${tipo}-input`}
          />
        </label>
        {has && (
          <button
            type="button"
            onClick={onRemove}
            className="w-7 h-7 rounded grid place-items-center text-[#B94A48] hover:bg-[#FCE4E4]"
            title="Remover"
            data-testid={`dtp-upload-${tipo}-remove`}
          >
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminCursoDTP() {
  const [params] = useSearchParams();
  const cursoId = params.get("curso") || "";
  const [data, setData] = useState({});
  const [initial, setInitial] = useState({});
  const [registry, setRegistry] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(null); // chapter_id em preview
  const [previewUrl, setPreviewUrl] = useState(null); // blob URL do PDF actual
  const [uploads, setUploads] = useState({}); // { registo_presencas: {path,url} }
  const [uploadBusy, setUploadBusy] = useState(null); // tipo em upload
  const [arquivando, setArquivando] = useState(false);
  const [arquivo, setArquivo] = useState(null); // {path, url, sha256, arquivado_em}

  const loadArquivo = useCallback(async () => {
    if (!cursoId) return;
    try {
      const r = await adminApi.cursoDtpArquivoGet(cursoId);
      setArquivo(r);
    } catch (e) { /* silent */ }
  }, [cursoId]);
  useEffect(() => { loadArquivo(); }, [loadArquivo]);

  const arquivar = async () => {
    if (dirty) { toast.error("Guarda os dados primeiro"); return; }
    setArquivando(true);
    try {
      const r = await adminApi.cursoDtpArquivar(cursoId);
      toast.success(`DTP arquivado (${r.chapters?.length || 0} capítulos, ${(r.size/1024).toFixed(0)} KB)`);
      if (r.erros?.length) {
        toast.warning(`${r.erros.length} capítulos com erro/skip — ver detalhe no relatório.`);
      }
      await loadArquivo();
    } catch (e) {
      toast.error(e.message || "Erro ao arquivar DTP");
    } finally { setArquivando(false); }
  };

  const loadUploads = useCallback(async () => {
    if (!cursoId) return;
    try {
      const [rp, pf, df] = await Promise.all([
        adminApi.cursoDtpUploadStatus(cursoId, "registo_presencas"),
        adminApi.cursoDtpUploadStatus(cursoId, "programa_formacao"),
        adminApi.cursoDtpUploadStatus(cursoId, "dtp_final_assinado"),
      ]);
      setUploads({ registo_presencas: rp, programa_formacao: pf, dtp_final_assinado: df });
    } catch (e) { /* silent */ }
  }, [cursoId]);
  useEffect(() => { loadUploads(); }, [loadUploads]);

  const uploadFile = async (tipo, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Só são aceites ficheiros PDF");
      return;
    }
    setUploadBusy(tipo);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(adminApi.cursoDtpUploadUrl(cursoId, tipo), {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erro ${res.status}`);
      }
      toast.success("Upload concluído");
      await loadUploads();
    } catch (e) {
      toast.error(e.message || "Erro no upload");
    } finally { setUploadBusy(null); }
  };

  const removeUpload = async (tipo) => {
    if (!confirm("Remover o documento carregado?")) return;
    try {
      await adminApi.cursoDtpUploadDelete(cursoId, tipo);
      toast.success("Documento removido");
      await loadUploads();
    } catch (e) {
      toast.error(e.message || "Erro ao remover");
    }
  };

  // Limpa o blob URL quando o modal fecha ou o componente desmonta
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const abrirPreview = async (ch) => {
    setPreviewing(ch.id);
    try {
      const res = await fetch(adminApi.cursoDtpPreviewUrl(cursoId, ch.id), {
        credentials: "include",
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `Erro ${res.status}`);
      }
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(e.message || "Erro a gerar preview");
      setPreviewing(null);
    }
  };

  const fecharPreview = () => {
    setPreviewing(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        adminApi.cursoDtpData(cursoId),
        adminApi.cursoDtpRegistry(cursoId),
      ]);
      // Converter datetimes para YYYY-MM-DD para inputs `date`
      const norm = { ...d };
      DATE_FIELDS.forEach((k) => {
        if (norm[k]) norm[k] = String(norm[k]).slice(0, 10);
      });
      setData(norm); setInitial(norm);
      setRegistry(r.capitulos || []);
      // Selecção default: só capítulos aplicáveis
      const sel = {};
      (r.capitulos || []).forEach((ch) => { sel[ch.id] = ch.aplicavel; });
      setSelected(sel);
    } catch (e) {
      toast.error(e.message || "Erro a carregar DTP");
    } finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { load(); }, [load]);

  const dirty = Object.keys(data).some((k) => (data[k] ?? "") !== (initial[k] ?? ""));

  const save = async () => {
    setSaving(true);
    try {
      // Só envia campos que mudaram
      const diff = {};
      Object.keys(data).forEach((k) => {
        if ((data[k] ?? "") !== (initial[k] ?? "")) diff[k] = data[k] || null;
      });
      await adminApi.cursoDtpDataSave(cursoId, diff);
      toast.success("Dados DTP guardados");
      setInitial({ ...data });
    } catch (e) {
      toast.error(e.message || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const gerar = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error("Seleciona pelo menos um capítulo"); return; }
    setGenerating(true);
    try {
      const res = await fetch(adminApi.cursoDtpGenerateUrl(cursoId), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `DTP_${cursoId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ZIP gerado com ${ids.length} capítulo(s)`);
    } catch (e) {
      toast.error(e.message || "Erro a gerar DTP");
    } finally { setGenerating(false); }
  };

  const setField = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  if (!cursoId) return <div className="p-6 text-[#6E6754]">Falta <code>?curso=&lt;ID&gt;</code>.</div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-curso-dtp-page">
      <CursoTabsNav cursoId={cursoId} current="dtp" />
      <header className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F0E7] grid place-items-center">
          <FolderCog className="w-5 h-5 text-[#A8843C]" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[#1B1B1B]">DTP — Curso #{cursoId}</h1>
          <p className="text-sm text-[#6E6754]">Dossier Técnico Pedagógico. Preenche os dados e gera os PDFs.</p>
        </div>
      </header>

      {loading ? <div className="py-6 flex items-center gap-2 text-[#6E6754]"><Loader2 className="w-4 h-4 animate-spin"/>A carregar…</div> : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Coluna 1-2: Formulário */}
          <div className="lg:col-span-2 space-y-4">
            {FIELD_GROUPS.map((grp) => (
              <section key={grp.title} className="bg-white border border-[#E6E1D6] rounded-md p-4">
                <h2 className="text-sm font-semibold text-[#A8843C] mb-3 uppercase tracking-wider">{grp.title}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {grp.fields.map((f) => (
                    <div key={f} className={LONG_TEXT.has(f) ? "col-span-2" : ""}>
                      <label className="block text-xs text-[#978D78] mb-1">{f}</label>
                      {LONG_TEXT.has(f) ? (
                        <textarea rows={3} className="w-full border border-[#E6E1D6] rounded px-2 py-1 text-sm focus:border-[#A8843C] focus:outline-none"
                          value={data[f] ?? ""} onChange={(e) => setField(f, e.target.value)} data-testid={`dtp-field-${f}`}/>
                      ) : (
                        <input type={DATE_FIELDS.has(f) ? "date" : "text"} className="w-full border border-[#E6E1D6] rounded px-2 py-1 text-sm focus:border-[#A8843C] focus:outline-none"
                          value={data[f] ?? ""} onChange={(e) => setField(f, e.target.value)} data-testid={`dtp-field-${f}`}/>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {/* Uploads DTP */}
            <section className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="dtp-uploads-section">
              <h2 className="text-sm font-semibold text-[#A8843C] mb-3 uppercase tracking-wider">Uploads DTP</h2>
              <UploadRow
                label="Registo de Presenças (Mod. 12)"
                hint="PDF do registo de presenças assinado pelos formandos e formador."
                tipo="registo_presencas"
                status={uploads.registo_presencas}
                busy={uploadBusy === "registo_presencas"}
                onUpload={(file) => uploadFile("registo_presencas", file)}
                onRemove={() => removeUpload("registo_presencas")}
              />
              <UploadRow
                label="Programa de Formação (Secção 3)"
                hint="PDF do programa da formação (objectivos, conteúdos, duração, avaliação)."
                tipo="programa_formacao"
                status={uploads.programa_formacao}
                busy={uploadBusy === "programa_formacao"}
                onUpload={(file) => uploadFile("programa_formacao", file)}
                onRemove={() => removeUpload("programa_formacao")}
              />
              <UploadRow
                label="DTP Definitivo (assinado e aprovado)"
                hint="PDF final do DTP consolidado, já assinado por Direção/Coordenação. Este é o documento oficial para auditorias."
                tipo="dtp_final_assinado"
                status={uploads.dtp_final_assinado}
                busy={uploadBusy === "dtp_final_assinado"}
                onUpload={(file) => uploadFile("dtp_final_assinado", file)}
                onRemove={() => removeUpload("dtp_final_assinado")}
              />
            </section>
            <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-[#E6E1D6] rounded-md p-3 flex items-center justify-between">
              <span className="text-xs text-[#978D78]">{dirty ? "Alterações não guardadas" : "Sem alterações"}</span>
              <button onClick={save} disabled={!dirty || saving} data-testid="dtp-save-btn"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold ${dirty ? "bg-[#A8843C] text-white hover:bg-[#8f6e2f]" : "bg-[#F3F0E7] text-[#978D78]"}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Guardar
              </button>
            </div>
          </div>

          {/* Coluna 3: Geração */}
          <aside className="space-y-4">
            <DtpAuditPanel cursoId={cursoId} />
            <section className="bg-white border border-[#E6E1D6] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#A8843C] mb-3 uppercase tracking-wider flex items-center gap-2">
                <Package className="w-4 h-4"/> Capítulos do DTP
              </h2>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {registry.map((ch) => (
                  <div key={ch.id} className={`flex items-start gap-2 p-1.5 rounded hover:bg-[#F3F0E7] text-sm ${!ch.aplicavel ? "opacity-50" : ""}`}>
                    <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer" title={ch.descricao}>
                      <input type="checkbox" checked={!!selected[ch.id]} disabled={!ch.aplicavel}
                        onChange={(e) => setSelected((s) => ({ ...s, [ch.id]: e.target.checked }))}
                        className="mt-0.5" data-testid={`dtp-chapter-${ch.id}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1B1B1B] truncate">{ch.codigo} — {ch.nome}</div>
                        <div className="text-[10px] text-[#978D78] line-clamp-2">{ch.descricao}</div>
                      </div>
                    </label>
                    <button
                      type="button"
                      onClick={() => abrirPreview(ch)}
                      disabled={!ch.aplicavel || previewing === ch.id}
                      title="Ver preview deste capítulo"
                      className="shrink-0 mt-0.5 w-7 h-7 rounded grid place-items-center text-[#A8843C] hover:bg-[#A8843C] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#A8843C] transition-colors"
                      data-testid={`dtp-preview-${ch.id}`}
                    >
                      {previewing === ch.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Eye className="w-3.5 h-3.5"/>}
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setSelected(Object.fromEntries(registry.filter((c) => c.aplicavel).map((c) => [c.id, true])))} className="text-xs text-[#A8843C] hover:underline">Todos aplicáveis</button>
                <button onClick={() => setSelected({})} className="text-xs text-[#978D78] hover:underline">Nenhum</button>
              </div>
              <button onClick={gerar} disabled={generating || dirty} data-testid="dtp-generate-btn"
                title={dirty ? "Guarda os dados primeiro" : "Gerar PDFs seleccionados"}
                className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-semibold ${dirty ? "bg-[#F3F0E7] text-[#978D78] cursor-not-allowed" : "bg-[#0F1829] text-white hover:bg-black"}`}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>} Gerar ZIP
              </button>

              {/* Arquivo final na CDN */}
              <div className="mt-4 pt-4 border-t border-[#E6E1D6]">
                <div className="text-[10px] uppercase tracking-wider text-[#978D78] font-semibold mb-2">
                  Arquivo final na CDN
                </div>
                {arquivo?.url ? (
                  <div className="text-xs mb-2 space-y-1">
                    <a href={arquivo.url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[#A8843C] hover:underline"
                       data-testid="dtp-arquivo-link">
                      <ExternalLink className="w-3 h-3"/> Descarregar ZIP arquivado
                    </a>
                    <div className="text-[10px] text-[#978D78]">
                      Arquivado {arquivo.arquivado_em?.slice(0,16).replace("T", " ")} UTC
                    </div>
                    <div className="text-[10px] text-[#978D78] font-mono break-all">
                      SHA-256: {arquivo.sha256}
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-[#978D78] italic mb-2">
                    Nunca foi arquivado.
                  </div>
                )}
                <button
                  onClick={arquivar}
                  disabled={arquivando || dirty}
                  data-testid="dtp-arquivar-btn"
                  title={dirty ? "Guarda os dados primeiro" : "Arquivar DTP final na CDN + SHA-256 na BD"}
                  className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-semibold border ${dirty ? "border-[#F3F0E7] text-[#978D78] cursor-not-allowed" : "border-[#A8843C] text-[#A8843C] hover:bg-[#A8843C] hover:text-white transition"}`}>
                  {arquivando ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
                  {arquivo?.url ? "Re-arquivar (novo snapshot)" : "Arquivar DTP final na CDN"}
                </button>
              </div>
            </section>
          </aside>
        </div>
      )}

      {/* Modal preview PDF */}
      {previewing && previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
          onClick={fecharPreview}
          data-testid="dtp-preview-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full h-full max-w-6xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E1D6]">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-[#A8843C] font-semibold">Preview DTP</div>
                <div className="text-sm text-[#1B1B1B] truncate">
                  {registry.find((c) => c.id === previewing)?.codigo} — {registry.find((c) => c.id === previewing)?.nome}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={previewUrl}
                  download={`DTP_${cursoId}_${previewing}.pdf`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-semibold bg-[#F3F0E7] text-[#0F1829] hover:bg-[#E6E1D6]"
                  data-testid="dtp-preview-download"
                >
                  <Download className="w-3.5 h-3.5"/> Descarregar
                </a>
                <button
                  type="button"
                  onClick={fecharPreview}
                  className="w-8 h-8 rounded grid place-items-center text-[#6E6754] hover:bg-[#F3F0E7] text-lg"
                  aria-label="Fechar preview"
                  data-testid="dtp-preview-close"
                >
                  ×
                </button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title="Preview PDF"
              className="flex-1 w-full border-0 bg-[#525659]"
              data-testid="dtp-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
